import React, { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { api, errorMessage } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { MailCheck, Loader2, X } from "lucide-react";
import { toast } from "sonner";

/**
 * Banner øverst i appen når e-postadressen ikke er bekreftet.
 * Viser hvorfor det er viktig (invitasjoner kobles først når adressen er bekreftet) og lar brukeren be om ny e-post.
 */
export const EmailVerifyBanner = () => {
  const { user, isOwner } = useAuth();
  const [busy, setBusy] = useState(false);
  const [hidden, setHidden] = useState(() => sessionStorage.getItem("rp_hide_verify_banner") === "1");

  if (!user || user.role !== "user" || user.email_verified || !isOwner || hidden) return null;

  const waiting = Number(user.unlinked_invitations || 0);
  const canSend = !!user.email_configured;

  const resend = async () => {
    setBusy(true);
    try {
      await api.post("/auth/resend-verification", null, { skipActAs: true });
      toast.success(`Bekreftelse sendt til ${user.email}`, { description: "Sjekk innboksen (og søppelpost) og klikk på lenken." });
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  const dismiss = () => {
    sessionStorage.setItem("rp_hide_verify_banner", "1");
    setHidden(true);
  };

  return (
    <div className="verify-banner border-t px-4 sm:px-6 lg:px-8 py-2 text-sm flex flex-wrap items-center gap-x-3 gap-y-1.5" role="status" data-testid="email-verify-banner">
      <MailCheck className="h-4 w-4 shrink-0" />
      <span className="flex-1 min-w-[200px]">
        Bekreft e-postadressen <strong>{user.email}</strong>.
        {waiting > 0 ? <> {waiting === 1 ? "1 invitasjon til fullmakt venter" : `${waiting} invitasjoner til fullmakt venter`} på bekreftelsen din.</> : null}
        {!canSend ? <span className="text-muted-foreground"> E-post er ikke satt opp av administrator ennå – bekreftelse blir mulig når det er på plass. Har du fått en invitasjonslenke, kan du åpne den mens du er innlogget.</span> : null}
      </span>
      <div className="flex items-center gap-1.5">
        {canSend ? (
          <Button size="sm" variant="outline" className="h-8" onClick={resend} disabled={busy} data-testid="email-verify-resend">
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : null} Send bekreftelse igjen
          </Button>
        ) : null}
        <button type="button" onClick={dismiss} className="h-8 w-8 inline-flex items-center justify-center rounded-md hover:bg-foreground/5 transition-colors" aria-label="Skjul" data-testid="email-verify-dismiss"><X className="h-4 w-4" /></button>
      </div>
    </div>
  );
};
